import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import clsx from 'clsx'
import React from 'react'
import { HeaderGroup } from 'react-table'
import CustomHoverTooltip from './CustomHoverTooltip'
import { sharedTableStyles } from './Spectable.styles'

const tableHeaderFilterRowStyles = makeStyles((theme: Theme) => ({
    tableResizer: {
        display: 'inline-block',
        right: 0,
        top: 0,
        position: 'absolute',
        cursor: 'col-resize',
        height: '100%',
        touchAction: 'none',
        width: 3,
        '&:hover': {
            backgroundColor: '#fff',
            width: 7,
        },
    },
    tableSortContainer: {
        '& .MuiTableSortLabel-root.MuiTableSortLabel-active.MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
            color: '#fff',
        },
    },
    headerTextFilterContainer: {
        height: '100%',
    },
    headerTextGridContainer: {
        height: 'inherit',
        backgroundColor: '#56ACF2',
        color: '#fff',
        '-moz-user-select': '-moz-none',
        '-khtml-user-select': 'none',
        '-webkit-user-select': 'none',
    },
    headerText: {
        textTransform: 'uppercase',
    },
    headerRow: {
        minHeight: 40,
        width: 'inherit',
    },
    headerFilterPaddingRight: {
        paddingRight: theme.spacing(1.5),
    },
    clearAllFilter: {
        margin: 0,
        padding: theme.spacing(0.25, 0),
    },
    clearFilterText: {
        cursor: 'pointer',
        padding: theme.spacing(0, 1.5),
        fontSize: pxToRem(13),
        margin: 0,
        fontWeight: 400,
        textAlign: 'left',
        lineHeight: 'normal',
        wordBreak: 'break-word',
        wordWrap: 'break-word',
        color: '#0098d6',
    },
    tableHeaderFilterRow: {
        '& .sticky-left-header': {
            boxShadow: '8px 0px 8px rgba(0, 0, 0, 0.10)',
        },
        '& .sticky-right-header': {
            boxShadow: '-8px 0px 8px rgba(0, 0, 0, 0.10)',
        },
        maxHeight: pxToRem(125),
    },
}))

export interface TableHeaderFilterRowProps<TData extends Object> {
    checkboxColumnWidth: number
    headerGroups: HeaderGroup<TData>[]
    showFilters: boolean
    clearAllFilters: (event: React.MouseEvent) => void
    isImportTable?: boolean
    sortBy: any
    inverseColumns: string[]
    defaultSortCount?: number
    hideCheckbox?: boolean
    cellCssOverride?: any // this SHould be CSSProps but the typescript is complaining
    [key: string]: any
}

const TableHeaderFilterRow = <TData extends Object>({
    checkboxColumnWidth,
    headerGroups,
    showFilters,
    clearAllFilters,
    isImportTable,
    sortBy,
    inverseColumns,
    defaultSortCount = 0,
    hideCheckbox,
    cellCssOverride,
}: TableHeaderFilterRowProps<TData>) => {
    const tableHeaderFilterRowClasses = tableHeaderFilterRowStyles()
    const sharedTableClasses = sharedTableStyles()

    return (
        <>
            {headerGroups.map((headerGroup) => {
                let leftStickyColumnIndex = 0,
                    rightStickyColumnIndex = 0

                return (
                    <div
                        {...headerGroup.getHeaderGroupProps()}
                        className={clsx(
                            'tr',
                            tableHeaderFilterRowClasses.tableHeaderFilterRow
                        )}
                    >
                        {headerGroup.headers.map((column, index) => {
                            if (column.sticky === 'left') {
                                leftStickyColumnIndex = index
                            } else if (
                                column.sticky === 'right' &&
                                !rightStickyColumnIndex
                            ) {
                                rightStickyColumnIndex = index
                            }

                            const header = column.render('Header')
                            const sortCols = sortBy?.split(',') ?? []
                            const sortCol = sortCols
                                .slice(
                                    0,
                                    sortCols.length - defaultSortCount > 0
                                        ? sortCols.length - defaultSortCount
                                        : sortCols.length
                                )
                                .find((col: string) => col.includes(column.id))

                            let sortingDirection: string | undefined = 'desc'

                            switch (true) {
                                case !!sortCol &&
                                    sortCol?.includes('desc') &&
                                    inverseColumns?.includes(column.id):
                                    sortingDirection = 'asc'
                                    break
                                case !!sortCol && sortCol?.includes('desc'):
                                    sortingDirection = 'desc'
                                    break
                                case !!sortCol &&
                                    inverseColumns?.includes(column.id):
                                    sortingDirection = 'desc'
                                    break
                                case !!sortCol:
                                    sortingDirection = 'asc'
                                    break
                                default:
                                    sortingDirection = undefined
                                    break
                            }

                            return (
                                <div
                                    {...column.getHeaderProps({
                                        style: {
                                            padding: 'unset',
                                            height: 'inherit',
                                            position: !column.sticky
                                                ? 'relative'
                                                : 'sticky',
                                            minWidth:
                                                column.sticky === 'left'
                                                    ? column?.stickyLeftMargin &&
                                                      column?.stickyLeftMargin! >
                                                          0
                                                        ? column.stickyLeftMargin
                                                        : checkboxColumnWidth
                                                    : (column as any)?.style
                                                    ? (column as any)?.style
                                                          ?.minWidth
                                                    : 'unset',
                                            left:
                                                column.sticky === 'left'
                                                    ? column.stickyLeftMargin ??
                                                      checkboxColumnWidth
                                                    : 'unset',
                                            right:
                                                column.sticky === 'right'
                                                    ? column.stickyRightMargin ??
                                                      0
                                                    : 'unset',
                                            zIndex: !column.sticky ? 0 : 1,
                                            ...cellCssOverride,
                                        },
                                    })}
                                    className={clsx(
                                        'th',
                                        leftStickyColumnIndex === index &&
                                            column.sticky === 'left' &&
                                            !hideCheckbox &&
                                            'sticky-left-header',
                                        rightStickyColumnIndex === index &&
                                            column.sticky === 'right' &&
                                            !hideCheckbox &&
                                            'sticky-right-header'
                                    )}
                                >
                                    <GridContainer
                                        direction="column"
                                        className={
                                            tableHeaderFilterRowClasses.headerTextFilterContainer
                                        }
                                    >
                                        <GridItem
                                            className={
                                                tableHeaderFilterRowClasses.headerRow
                                            }
                                            style={{ flex: 1 }}
                                        >
                                            <GridContainer
                                                {...column.getSortByToggleProps(
                                                    {
                                                        title: undefined,
                                                    }
                                                )}
                                                justifyContent="space-between"
                                                alignItems="center"
                                                className={clsx(
                                                    tableHeaderFilterRowClasses.headerTextGridContainer,
                                                    sharedTableClasses.cellPadding
                                                )}
                                                style={{
                                                    minHeight: 40,
                                                    height: '100%',
                                                    cursor: isImportTable
                                                        ? 'default'
                                                        : column.canSort
                                                        ? 'pointer'
                                                        : 'default',
                                                }}
                                            >
                                                <GridItem
                                                    xs={
                                                        column.canSort ? 11 : 12
                                                    }
                                                    className={clsx(
                                                        sharedTableClasses.textOverflowEllipsis,
                                                        tableHeaderFilterRowClasses.headerText,
                                                        'header-text-container'
                                                    )}
                                                >
                                                    {typeof header ===
                                                    'string' ? (
                                                        <CustomHoverTooltip
                                                            text={header}
                                                        />
                                                    ) : column.id ===
                                                          'checkboxCol' &&
                                                      hideCheckbox ? (
                                                        <></>
                                                    ) : (
                                                        header
                                                    )}
                                                </GridItem>
                                                {column.canSort && (
                                                    <GridItem
                                                        xs={1}
                                                        className={clsx(
                                                            tableHeaderFilterRowClasses.tableSortContainer,
                                                            'sort-label-container'
                                                        )}
                                                    >
                                                        {column.id !==
                                                            'selection' && (
                                                            <TableSortLabel
                                                                active={
                                                                    !!sortCol
                                                                }
                                                                direction={
                                                                    sortingDirection as
                                                                        | 'desc'
                                                                        | 'asc'
                                                                        | undefined
                                                                }
                                                            />
                                                        )}
                                                    </GridItem>
                                                )}
                                            </GridContainer>
                                        </GridItem>
                                        {showFilters && (
                                            <GridItem
                                                className={clsx(
                                                    tableHeaderFilterRowClasses.headerRow,
                                                    sharedTableClasses.cellPadding,
                                                    tableHeaderFilterRowClasses.headerFilterPaddingRight,
                                                    'header-filter-container',
                                                    column.id === 'actionCol' &&
                                                        tableHeaderFilterRowClasses.clearAllFilter
                                                )}
                                            >
                                                {column.canFilter &&
                                                    column.render('Filter')}
                                                {column.id === 'actionCol' &&
                                                    !!clearAllFilters && (
                                                        <Button
                                                            disableRipple
                                                            onClick={
                                                                clearAllFilters
                                                            }
                                                            className={clsx(
                                                                tableHeaderFilterRowClasses.clearFilterText,
                                                                'clear-all-filters'
                                                            )}
                                                        >
                                                            Clear Filters
                                                        </Button>
                                                    )}
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                    {column.canResize &&
                                        !['actionCol', 'checkboxCol'].includes(
                                            column.id
                                        ) && (
                                            <div
                                                {...column.getResizerProps()}
                                                className={clsx(
                                                    tableHeaderFilterRowClasses.tableResizer,
                                                    column.isResizing &&
                                                        'resizing',
                                                    'header-resize-container'
                                                )}
                                            />
                                        )}
                                </div>
                            )
                        })}
                    </div>
                )
            })}
        </>
    )
}

export default TableHeaderFilterRow
